import React, { useState } from 'react';
import { Paper, Text, Group, Table, ScrollArea, Loader, Badge, SimpleGrid, Box, Collapse, UnstyledButton, Tooltip } from '@mantine/core';
import { IconChevronRight, IconChevronDown } from '@tabler/icons-react';
import useAuthenticatedData from '../../../hooks/useAuthenticatedData';
import config from '../../../config';

const BASE = config.apiBaseUrl;
// SSP expiry by ABC class: A = 90 days, B/C/D = 180 days.
const PRICE_AGE_LIMIT = { A: 90, B: 180, C: 180, D: 180 };
export const fmtDate = (dt) => (dt ? new Date(dt).toLocaleDateString() : '—');
export const fmtMoney = (v) => (v != null && v !== 0 ? `$${Number(v).toLocaleString()}` : '—');
const sameAcct = (a, b) => a != null && b != null && String(a).trim() === String(b).trim();
export function priceExpired(days, cls) {
  if (days == null || cls == null) return false;
  const limit = PRICE_AGE_LIMIT[String(cls).trim().toUpperCase()];
  return limit != null && days > limit;
}

// Compact ABC-class chip shown next to a part number everywhere it appears.
const CLASS_COLOR = { A: 'grape', B: 'blue', C: 'teal', D: 'gray' };
export function ClassBadge({ cls, size = 'xs' }) {
  const c = cls ? String(cls).trim().toUpperCase() : '';
  if (!c) return null;
  return <Badge size={size} variant="light" color={CLASS_COLOR[c] || 'gray'} title={`Class ${c}`}>{c}</Badge>;
}
// Part description, truncated to keep the (now half-width) lines table readable. 22 chars fits ~92% of
// real descriptions whole; the rest get an ellipsis and the full text on hover. ~14% of lines have none.
const DESC_CAP = 22;
export function DescCell({ text }) {
  const s = text ? String(text).trim() : '';
  if (!s) return <Text size="xs" c="dimmed">—</Text>;
  if (s.length <= DESC_CAP) return <Text size="xs" style={{ whiteSpace: 'nowrap' }}>{s}</Text>;
  return (
    <Tooltip label={s} multiline w={280} withArrow>
      <Text size="xs" style={{ whiteSpace: 'nowrap' }}>{s.slice(0, DESC_CAP).trimEnd()}…</Text>
    </Tooltip>
  );
}

const partUrl = (panel, pn, extra = '') =>
  pn ? `${BASE}/sales/part-detail/${panel}?partNumber=${encodeURIComponent(pn)}${extra}` : null;

function PanelShell({ title, count, extra, children }) {
  return (
    <Paper withBorder p="xs" radius="md">
      <Group gap={6} mb={4} px="xs" wrap="nowrap">
        <Text fw={600} size="sm">{title}{count != null ? ` (${count})` : ''}</Text>
        {extra}
      </Group>
      {children}
    </Paper>
  );
}
function Grid({ headers, rows, loading, empty, h = 240 }) {
  return (
    <ScrollArea h={h}>
      {loading ? <Box py="md" ta="center"><Loader size="sm" /></Box> : (
        <Table verticalSpacing={3} horizontalSpacing="xs" style={{ fontSize: 12 }} stickyHeader highlightOnHover>
          <Table.Thead><Table.Tr>{headers.map((h2) => (
            <Table.Th key={h2.key} style={{ whiteSpace: 'nowrap', textAlign: h2.right ? 'right' : undefined }}>{h2.label}</Table.Th>
          ))}</Table.Tr></Table.Thead>
          <Table.Tbody>
            {rows.length === 0
              ? <Table.Tr><Table.Td colSpan={headers.length}><Text c="dimmed" ta="center" py="sm">{empty}</Text></Table.Td></Table.Tr>
              : rows}
          </Table.Tbody>
        </Table>
      )}
    </ScrollArea>
  );
}
// Highlight rows for THIS deal's customer.
const mineStyle = { background: 'var(--mantine-color-teal-light)' };

// Subtle per-condition row tint for on-hand inventory (new→green, overhauled→teal,
// serviceable→blue, as-removed→orange). Unknown conditions get no tint.
const CONDITION_TINT = { NE: 'green', NS: 'green', FN: 'green', OH: 'teal', SV: 'blue', AR: 'orange' };
const condTint = (c) => {
  const k = (c || '').trim().toUpperCase();
  return CONDITION_TINT[k] ? `var(--mantine-color-${CONDITION_TINT[k]}-light)` : undefined;
};

// REALIZED sales (realized=1): shipped, revenue-contributing invoices from the last 3 years — the exact rows
// the pricing engine takes its comp price from. This panel used to render sales ORDERS, so it could
// "confirm" a last-sale price Nymeria quoted that was actually an order which never shipped (62 order rows vs
// 16 realized, on one sample part). The engine prices off invoices, so the rep sees invoices.
export function SalesHistoryPanel({ partNumber, acctno, partClass }) {
  const { data, isLoading } = useAuthenticatedData(partUrl('sales-history', partNumber, '&realized=1'));
  const rows = Array.isArray(data) ? data : [];
  return (
    <PanelShell
      title="Sales History — realized"
      count={Array.isArray(data) ? rows.length : undefined}
      extra={
        <Tooltip label="Shipped, revenue-contributing sales from the last 3 years — the same sales the quoted price is anchored on." withArrow multiline w={280}>
          <Text size="xs" c="dimmed" style={{ cursor: 'help' }}>?</Text>
        </Tooltip>
      }
    >
      <Grid loading={isLoading} empty="No realized sales." headers={[
        { key: 'd', label: 'Shipped' }, { key: 'cust', label: 'Customer' }, { key: 'k', label: 'Key' },
        { key: 'c', label: 'Cond' }, { key: 'q', label: 'Qty', right: true }, { key: 'u', label: 'Unit $', right: true },
        { key: 's', label: 'SSP', right: true }, { key: 'pa', label: 'Px Age', right: true }, { key: 'dos', label: 'DOS', right: true },
      ]} rows={rows.map((r, i) => {
        const mine = sameAcct(r.acctNo, acctno);
        return (
          <Table.Tr key={i} style={mine ? mineStyle : undefined}>
            <Table.Td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.addedOnDate)}</Table.Td>
            <Table.Td style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.accountName}>
              {mine && <Badge size="xs" color="teal" variant="filled" mr={4}>this</Badge>}{r.accountName}
            </Table.Td>
            <Table.Td>{r.isKeyAccount ? <Badge size="xs" color="teal" variant="light">Y</Badge> : null}</Table.Td>
            <Table.Td>{r.condition?.toUpperCase()}</Table.Td>
            <Table.Td ta="right">{r.qty}</Table.Td>
            <Table.Td ta="right">{fmtMoney(r.unitPrice)}</Table.Td>
            <Table.Td ta="right">{fmtMoney(r.sspAtTime)}</Table.Td>
            <Table.Td ta="right"><Text size="sm" c={priceExpired(r.priceAge, partClass) ? 'red' : undefined}>{r.priceAge != null ? r.priceAge.toLocaleString() : '—'}</Text></Table.Td>
            <Table.Td ta="right">{r.dos != null ? r.dos.toLocaleString() : ''}</Table.Td>
          </Table.Tr>
        );
      })} />
    </PanelShell>
  );
}

// The 3-year window (allyears=0) — the same window the judge and Nymeria reason over. The panel used to pull
// ALL time while the judge scored 3 years, so the AI could say "we've quoted this 47 times" over a history the
// judgement that GATES it never saw.
export function QuoteHistoryPanel({ partNumber, acctno, partClass }) {
  const { data, isLoading } = useAuthenticatedData(partUrl('quote-history', partNumber));
  const rows = Array.isArray(data) ? data : [];
  return (
    <PanelShell
      title="Quote History — 3 yr"
      count={Array.isArray(data) ? rows.length : undefined}
      extra={
        <Tooltip label="The last 3 years — the same window the AI's scoring and recommendation reason over." withArrow multiline w={260}>
          <Text size="xs" c="dimmed" style={{ cursor: 'help' }}>?</Text>
        </Tooltip>
      }
    >
      <Grid loading={isLoading} empty="No quote history." headers={[
        { key: 'd', label: 'Date' }, { key: 'cust', label: 'Customer' }, { key: 'k', label: 'Key' },
        { key: 'c', label: 'Cond' }, { key: 'q', label: 'Qty', right: true }, { key: 'u', label: 'Unit $', right: true },
        { key: 's', label: 'SSP', right: true }, { key: 'pa', label: 'Px Age', right: true }, { key: 'conv', label: 'Conv' },
      ]} rows={rows.map((r, i) => {
        const mine = sameAcct(r.acctNo, acctno);
        return (
          <Table.Tr key={i} style={mine ? mineStyle : undefined}>
            <Table.Td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.quotedDate)}</Table.Td>
            <Table.Td style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.accountName}>
              {mine && <Badge size="xs" color="teal" variant="filled" mr={4}>this</Badge>}{r.accountName}
            </Table.Td>
            <Table.Td>{r.isKeyAccount ? <Badge size="xs" color="teal" variant="light">Y</Badge> : null}</Table.Td>
            <Table.Td>{r.condition?.toUpperCase()}</Table.Td>
            <Table.Td ta="right">{r.qty}</Table.Td>
            <Table.Td ta="right">{fmtMoney(r.unitPrice)}</Table.Td>
            <Table.Td ta="right">{fmtMoney(r.sspAtTime)}</Table.Td>
            <Table.Td ta="right"><Text size="sm" c={priceExpired(r.priceAge, partClass) ? 'red' : undefined}>{r.priceAge != null ? r.priceAge.toLocaleString() : '—'}</Text></Table.Td>
            <Table.Td>{r.converted ? '✓' : ''}</Table.Td>
          </Table.Tr>
        );
      })} />
    </PanelShell>
  );
}

export function InventoryPanel({ partNumber }) {
  const { data, isLoading } = useAuthenticatedData(partUrl('inventory', partNumber));
  const rows = Array.isArray(data) ? data : [];
  return (
    <PanelShell title="On-Hand Inventory" count={Array.isArray(data) ? rows.length : undefined}>
      <Grid loading={isLoading} empty="No on-hand inventory." headers={[
        { key: 'sn', label: 'Serial' }, { key: 'c', label: 'Cond' }, { key: 'pwh', label: 'Phys WH' }, { key: 'vwh', label: 'Lot WH' },
        { key: 'rc', label: 'Received' }, { key: 'dos', label: 'DOS', right: true }, { key: 'llp', label: 'Min $', right: true }, { key: 'cost', label: 'Cost', right: true },
      ]} rows={rows.map((r, i) => (
        <Table.Tr key={i} style={{ background: condTint(r.condition) }}>
          <Table.Td style={{ whiteSpace: 'nowrap' }}>{r.logNo}</Table.Td>
          <Table.Td>{r.condition?.toUpperCase()}</Table.Td>
          <Table.Td>{r.physicalWarehouseCode}</Table.Td>
          <Table.Td>{r.virtualWarehouseCode}</Table.Td>
          <Table.Td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.receiverDate)}</Table.Td>
          <Table.Td ta="right">{r.dos != null ? r.dos.toLocaleString() : ''}</Table.Td>
          <Table.Td ta="right">{r.llp != null && r.llp !== 0 ? fmtMoney(r.llp) : '—'}</Table.Td>
          <Table.Td ta="right">{fmtMoney(r.cost)}</Table.Td>
        </Table.Tr>
      ))} />
    </PanelShell>
  );
}

export function PricingPanel({ partNumber }) {
  const { data: pf } = useAuthenticatedData(partUrl('part-facts', partNumber));
  const { data: ph, isLoading } = useAuthenticatedData(partUrl('pricing-history', partNumber));
  const pd = pf ?? {};
  const rows = Array.isArray(ph) ? ph : [];
  const [showChanges, setShowChanges] = useState(false);
  const ilsAgo = pd.ilsDate ? Math.round((Date.now() - new Date(pd.ilsDate)) / 86400000) : null;
  const sspRow = (label, price, dated, days) => {
    const expired = price != null && price !== 0 && priceExpired(days, pd.class);
    return (
      <Table.Tr>
        <Table.Td><Text size="sm" fw={500}>{label}</Text></Table.Td>
        <Table.Td ta="right"><Text size="sm" c={expired ? 'red' : undefined} fw={expired ? 700 : undefined}>{fmtMoney(price)}</Text></Table.Td>
        <Table.Td>
          <Text size="xs" c={expired ? 'red' : 'dimmed'}>
            {dated ? fmtDate(dated) : '—'}{days != null ? ` (${days}d)` : ''}{expired ? ' · expired' : ''}
          </Text>
        </Table.Td>
      </Table.Tr>
    );
  };
  return (
    <PanelShell title="Pricing — SSP by condition" extra={<ClassBadge cls={pd.class} />}>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        <Table verticalSpacing={4} horizontalSpacing="xs" style={{ fontSize: 12 }}>
          <Table.Thead><Table.Tr><Table.Th /><Table.Th ta="right"><Text size="xs" c="dimmed">Current</Text></Table.Th><Table.Th><Text size="xs" c="dimmed">Updated</Text></Table.Th></Table.Tr></Table.Thead>
          <Table.Tbody>
            {sspRow('AR', pd.arSellPrice, pd.arPricedDate, pd.daysSinceArPricedDate)}
            {sspRow('SV', pd.svSellPrice, pd.svPricedDate, pd.daysSinceSvPricedDate)}
            {sspRow('OH', pd.ohSellPrice, pd.ohPricedDate, pd.daysSinceOhPricedDate)}
            {sspRow('New', pd.newSellPrice, pd.newPricedDate, pd.daysSinceNewPricedDate)}
          </Table.Tbody>
        </Table>
        <div>
          <Group justify="space-between" mb={2}><Text size="xs" fw={600}>ILS market</Text><Text size="xs" c="dimmed">{pd.ilsDate ? `${fmtDate(pd.ilsDate)}${ilsAgo != null ? ` (${ilsAgo}d)` : ''}` : '—'}</Text></Group>
          <SimpleGrid cols={2} spacing={4}>
            <Text size="xs" c="dimmed">EUI: <b>{pd.ilsEndUserInquiry ?? '—'}</b></Text>
            <Text size="xs" c="dimmed">Total: <b>{pd.ilsTotalInquiries ?? '—'}</b></Text>
            <Text size="xs" c="dimmed">Non-EUI: <b>{pd.ilsNonEndUserInquiry ?? '—'}</b></Text>
            <Text size="xs" c="dimmed">Avg src: <b>{pd.ilsAverageSources != null ? pd.ilsAverageSources.toFixed(1) : '—'}</b></Text>
          </SimpleGrid>
        </div>
      </SimpleGrid>
      <UnstyledButton onClick={() => setShowChanges((o) => !o)} mt="sm" px="xs" style={{ width: '100%' }}>
        <Group gap={4}>
          {showChanges ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
          <Text fw={600} size="xs">Pricing changes{Array.isArray(ph) ? ` (${rows.length})` : ''}</Text>
        </Group>
      </UnstyledButton>
      <Collapse in={showChanges}>
        <Grid h={140} loading={isLoading} empty="No pricing history." headers={[
          { key: 'c', label: 'Cond' }, { key: 'f', label: 'From' }, { key: 't', label: 'To' }, { key: 'p', label: 'Price', right: true }, { key: 'a', label: 'Prev age', right: true },
        ]} rows={rows.map((r, i) => (
          <Table.Tr key={i}>
            <Table.Td>{r.condition}</Table.Td>
            <Table.Td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.validFrom)}</Table.Td>
            <Table.Td style={{ whiteSpace: 'nowrap' }}>{r.validTo ? fmtDate(r.validTo) : '—'}</Table.Td>
            <Table.Td ta="right">{fmtMoney(r.price)}</Table.Td>
            <Table.Td ta="right">{r.priceAge != null ? `${r.priceAge.toLocaleString()}d` : '—'}</Table.Td>
          </Table.Tr>
        ))} />
      </Collapse>
    </PanelShell>
  );
}

export function RepairPanel({ partNumber }) {
  const { data, isLoading } = useAuthenticatedData(partUrl('repair-history', partNumber));
  const rows = Array.isArray(data) ? data : [];
  const cond = (x) => (x ? String(x).toUpperCase() : '—');
  return (
    <PanelShell title="Repair History" count={Array.isArray(data) ? rows.length : undefined}>
      <Grid h={200} loading={isLoading} empty="No repair history for this part." headers={[
        { key: 'sn', label: 'Serial' }, { key: 'e', label: 'Entered' }, { key: 's', label: 'Shipped' }, { key: 'r', label: 'Received' },
        { key: 'tat', label: 'TaT', right: true }, { key: 'v', label: 'Vendor' }, { key: 'c', label: 'Cost', right: true },
        { key: 'oc', label: 'Out→Exp→Rcvd' }, { key: 'ber', label: 'BER' },
      ]} rows={rows.map((r, i) => (
        <Table.Tr key={i}>
          <Table.Td style={{ whiteSpace: 'nowrap' }}>{r.logNo}</Table.Td>
          <Table.Td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.hdrEnterDate)}</Table.Td>
          <Table.Td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.shippedOn)}</Table.Td>
          <Table.Td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.dateReceived)}</Table.Td>
          <Table.Td ta="right">{r.taT ?? ''}</Table.Td>
          <Table.Td style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.vendorName}>{r.vendorName}</Table.Td>
          <Table.Td ta="right">{fmtMoney(r.repairCost)}</Table.Td>
          <Table.Td style={{ whiteSpace: 'nowrap' }}>{cond(r.shippedOutCondition)}→{cond(r.expectedRepairedCondition)}→{cond(r.receivedCondition)}</Table.Td>
          <Table.Td>{r.isBer === 'Y' ? <Badge size="xs" color="red" variant="light">BER</Badge> : ''}</Table.Td>
        </Table.Tr>
      ))} />
    </PanelShell>
  );
}

// OPEN DEMAND — who ELSE is chasing this line's part right now: our own open quotes plus the inbound RFQ
// stream, across OTHER deals (this deal is excluded by construction).
//
// This is the one legitimate cross-part signal in the whole pipeline — opportunity cost. Nymeria has always
// been given it and told to let it move the call ("Delta has an open quote on this part at $4,200; don't
// undercut it") — but there was no panel for it anywhere, so the fact most likely to change the rep's
// decision was also the one fact they could not check. An AI claim a rep cannot verify is worse than no
// claim: they stop trusting the ones that are right.
//
// Same view, same cap (12), same self-deal exclusion the model gets — so the rows here ARE the rows it saw.
export function OpenDemandPanel({ dealPublicId, dealLineId }) {
  const url = dealPublicId && dealLineId
    ? `${BASE}/api/vesper/deals/${encodeURIComponent(dealPublicId)}/lines/${dealLineId}/open-demand`
    : null;
  const { data, isLoading } = useAuthenticatedData(url);
  const rows = Array.isArray(data?.openDemand) ? data.openDemand : [];

  const headers = [
    { key: 's', label: 'Source' }, { key: 'c', label: 'Customer' }, { key: 'q', label: 'Qty', right: true },
    { key: 'u', label: 'Unit $', right: true }, { key: 'd', label: 'Date' },
  ];
  const body = rows.map((r, i) => (
    <Table.Tr key={i}>
      <Table.Td>
        <Badge size="xs" variant="light" color={r.source === 'quote' ? 'blue' : 'grape'}>
          {r.source || '—'}
        </Badge>
      </Table.Td>
      <Table.Td style={{ maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.account_name}>
        <Group gap={4} wrap="nowrap">
          <Text size="xs" span>{r.account_name || r.acctno || '—'}</Text>
          {r.is_key_customer === 'Y' && <Badge size="xs" variant="light" color="yellow" title="Key customer">key</Badge>}
        </Group>
      </Table.Td>
      <Table.Td ta="right">{r.qty ?? ''}</Table.Td>
      <Table.Td ta="right">{fmtMoney(r.unit_price)}</Table.Td>
      <Table.Td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.doc_date)}</Table.Td>
    </Table.Tr>
  ));

  return (
    <PanelShell
      title="Open demand — who else wants it"
      count={data ? rows.length : undefined}
      extra={
        <Tooltip
          label="Other deals chasing this same part right now (our open quotes + inbound RFQs), excluding this one. This is the opportunity-cost signal the AI weighs."
          withArrow
          multiline
          w={280}
        >
          <Text size="xs" c="dimmed" style={{ cursor: 'help' }}>?</Text>
        </Tooltip>
      }
    >
      <Grid h={150} loading={isLoading} empty="Nobody else is open on this part." headers={headers} rows={body} />
    </PanelShell>
  );
}

// PRICING PROBES for the part. ITS records market research through Pentagon's RFQ feature (it timestamps
// them cleanly), so these are not really requests-for-quote. Two kinds:
//   · MARKET PRICING — what our COMPETITORS sell this part for. This is a COMPARABLE, like our own sales
//     history — it is NOT our cost. It used to be labelled "market cost — vendor buy quotes", and that read
//     straight through to the AI prompt, which told the model to treat it as a cost floor. Competitor asks
//     run ~1.4x our own realized sell price, so the label alone was pushing quotes up.
//   · REPAIR — what a repair costs. That one IS a cost, and it floors the engine's AR price.
// (A PO is how ITS actually buys parts, and it is not shown here.)
export function RfqPanel({ partNumber }) {
  const { data, isLoading } = useAuthenticatedData(partNumber ? `${BASE}/api/vesper/part/${encodeURIComponent(partNumber)}/rfqs` : null);
  const market = Array.isArray(data?.marketPricing) ? data.marketPricing : [];
  const repair = Array.isArray(data?.repairQuotes) ? data.repairQuotes : [];
  const avg = Array.isArray(data?.repairAvg) ? data.repairAvg : [];
  const yes = (v) => v === 1 || v === true;
  const headers = [
    { key: 'v', label: 'Vendor' }, { key: 'c', label: 'Cond' }, { key: 'req', label: 'Req', right: true },
    { key: 'bid', label: 'Bid', right: true }, { key: 'u', label: 'Unit $', right: true }, { key: 'd', label: 'Date' }, { key: 'po', label: 'PO' },
  ];
  const rowsOf = (arr) => arr.map((r, i) => (
    <Table.Tr key={i} style={condTint(r.condition) ? { background: condTint(r.condition) } : undefined}>
      <Table.Td style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.vendor}>{r.vendor || '—'}</Table.Td>
      <Table.Td>{r.condition ? String(r.condition).toUpperCase() : '—'}</Table.Td>
      <Table.Td ta="right">{r.qty_req ?? ''}</Table.Td>
      <Table.Td ta="right">{r.qty_bid ?? ''}</Table.Td>
      <Table.Td ta="right">{fmtMoney(r.unit_price)}</Table.Td>
      <Table.Td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.added_on_date)}</Table.Td>
      <Table.Td>{yes(r.converted) ? '✓' : ''}</Table.Td>
    </Table.Tr>
  ));
  return (
    <PanelShell title="Pricing probes — market intel" count={data ? market.length + repair.length : undefined}>
      {avg.length > 0 && (
        <Group gap="sm" px="xs" mb={6}>
          <Text size="xs" fw={600}>Repair avg:</Text>
          {avg.map((a, i) => (
            <Text key={i} size="xs" c={yes(a.expired) ? 'red' : 'dimmed'}>
              {a.condition ? String(a.condition).toUpperCase() : '—'} {fmtMoney(a.avg_repair_cost)}{a.days_old != null ? ` (${a.days_old}d)` : ''}{yes(a.expired) ? ' · expired' : ''}
            </Text>
          ))}
        </Group>
      )}
      <Tooltip
        label="What COMPETITORS sell this part for — a comparable, like our own sales. Not our cost, and not a price floor."
        withArrow
        multiline
        w={280}
      >
        <Text fw={600} size="xs" px="xs" mb={2} style={{ width: 'fit-content' }}>
          Market pricing — what competitors sell it for ({market.length})
        </Text>
      </Tooltip>
      <Grid h={150} loading={isLoading} empty="No market-pricing probes." headers={headers} rows={rowsOf(market)} />
      <Tooltip label="What vendors say a repair costs. This one IS a cost — it floors the AR price." withArrow multiline w={280}>
        <Text fw={600} size="xs" px="xs" mt="sm" mb={2} style={{ width: 'fit-content' }}>
          Repair — what a repair costs ({repair.length})
        </Text>
      </Tooltip>
      <Grid h={150} loading={isLoading} empty="No repair probes." headers={headers} rows={rowsOf(repair)} />
    </PanelShell>
  );
}
